import React from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import {
  AnioEscolar,
  GrupoContenido,
  TemaContenido,
  UnidadContenido,
} from "../../../services/admin/malla-curricular";

interface ContenidoCurricularFormProps {
  isEditing: boolean;
  formData: {
    anioEscolarId: number;
    unidadContenidoId: number;
    temaContenidoId: number;
    grupoContenidoId: number;
    conceptosPrevios: string;
    explicacion: string;
    ejemplo: string;
    solucion: string;
    dificultad: number;
    aplicacionPractica: string;
    fecha?: string;
    imagen?: File | undefined;
  };
  setFormData: React.Dispatch<
    React.SetStateAction<{
      anioEscolarId: number;
      unidadContenidoId: number;
      temaContenidoId: number;
      grupoContenidoId: number;
      conceptosPrevios: string;
      explicacion: string;
      ejemplo: string;
      solucion: string;
      dificultad: number;
      aplicacionPractica: string;
      fecha?: string;
      imagen?: File | undefined;
    }>
  >;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  aniosEscolares: AnioEscolar[];
  unidadesContenido: UnidadContenido[];
  temasContenido: TemaContenido[];
  gruposContenido: GrupoContenido[];
}

export const ContenidoCurricularForm: React.FC<
  ContenidoCurricularFormProps
> = ({
  isEditing,
  formData,
  setFormData,
  onSubmit,
  onCancel,
  aniosEscolares,
  unidadesContenido,
  temasContenido,
  gruposContenido,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="anioEscolar">Año Escolar *</Label>
          <Select
            value={formData.anioEscolarId.toString()}
            onValueChange={(value) =>
              setFormData({
                ...formData,
                anioEscolarId: parseInt(value),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar año escolar" />
            </SelectTrigger>
            <SelectContent>
              {aniosEscolares.map((anio) => (
                <SelectItem
                  key={anio.idAnioEscolar}
                  value={anio.idAnioEscolar.toString()}
                >
                  {anio.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="unidadContenido">Unidad de Contenido *</Label>
          <Select
            value={formData.unidadContenidoId.toString()}
            onValueChange={(value) =>
              setFormData({
                ...formData,
                unidadContenidoId: parseInt(value),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar unidad" />
            </SelectTrigger>
            <SelectContent>
              {unidadesContenido.map((unidad) => (
                <SelectItem
                  key={unidad.idUnidadContenido}
                  value={unidad.idUnidadContenido.toString()}
                >
                  {unidad.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="temaContenido">Tema de Contenido *</Label>
          <Select
            value={formData.temaContenidoId.toString()}
            onValueChange={(value) =>
              setFormData({
                ...formData,
                temaContenidoId: parseInt(value),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar tema" />
            </SelectTrigger>
            <SelectContent>
              {temasContenido.map((tema) => (
                <SelectItem
                  key={tema.idTemaContenido}
                  value={tema.idTemaContenido.toString()}
                >
                  {tema.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="grupoContenido">Grupo de Contenido (Opcional)</Label>
          <Select
            value={formData.grupoContenidoId.toString()}
            onValueChange={(value) =>
              setFormData({
                ...formData,
                grupoContenidoId: parseInt(value),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar grupo (opcional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Sin grupo</SelectItem>
              {gruposContenido.map((grupo) => (
                <SelectItem
                  key={grupo.idGrupoContenido}
                  value={grupo.idGrupoContenido.toString()}
                >
                  {grupo.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="conceptosPrevios">Conceptos Previos</Label>
        <Textarea
          id="conceptosPrevios"
          value={formData.conceptosPrevios}
          onChange={(e) =>
            setFormData({
              ...formData,
              conceptosPrevios: e.target.value,
            })
          }
          placeholder="Conceptos previos necesarios..."
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="explicacion">Explicación</Label>
        <Textarea
          id="explicacion"
          value={formData.explicacion}
          onChange={(e) =>
            setFormData({
              ...formData,
              explicacion: e.target.value,
            })
          }
          placeholder="Explicación del contenido..."
          rows={4}
        />
      </div>

      <div>
        <Label htmlFor="ejemplo">Ejemplo</Label>
        <Textarea
          id="ejemplo"
          value={formData.ejemplo}
          onChange={(e) =>
            setFormData({
              ...formData,
              ejemplo: e.target.value,
            })
          }
          placeholder="Ejemplo práctico..."
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="solucion">Solución</Label>
        <Textarea
          id="solucion"
          value={formData.solucion}
          onChange={(e) =>
            setFormData({
              ...formData,
              solucion: e.target.value,
            })
          }
          placeholder="Solución del ejemplo..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="dificultad">Dificultad</Label>
          <Select
            value={formData.dificultad.toString()}
            onValueChange={(value) =>
              setFormData({
                ...formData,
                dificultad: parseInt(value),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar dificultad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 - Muy Fácil</SelectItem>
              <SelectItem value="2">2 - Fácil</SelectItem>
              <SelectItem value="3">3 - Intermedio</SelectItem>
              <SelectItem value="4">4 - Difícil</SelectItem>
              <SelectItem value="5">5 - Muy Difícil</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="fecha">Fecha</Label>
          <Input
            id="fecha"
            type="date"
            value={formData.fecha || ""}
            onChange={(e) =>
              setFormData({ ...formData, fecha: e.target.value })
            }
          />
        </div>
      </div>

      <div>
        <Label htmlFor="aplicacionPractica">Aplicación Práctica</Label>
        <Textarea
          id="aplicacionPractica"
          value={formData.aplicacionPractica}
          onChange={(e) =>
            setFormData({
              ...formData,
              aplicacionPractica: e.target.value,
            })
          }
          placeholder="Aplicación práctica del contenido..."
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="imagen">Imagen (Opcional)</Label>
        <Input
          id="imagen"
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setFormData({ ...formData, imagen: file });
            }
          }}
        />
      </div>

      <div className="flex space-x-2 pt-4">
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
          {isEditing ? "Actualizar" : "Crear"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
};
